<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import {
  DuplicateContactException,
} from 'shared/helpers/CustomErrors';
import ContactAPI from 'dashboard/api/contacts';
import MessageApi from 'dashboard/api/inbox/message';

const emit = defineEmits(['close']);
const store = useStore();
const router = useRouter();
const { accountId } = useAccount();

const currentUser = computed(() => store.getters.getCurrentUser);
const currentRole = computed(() => store.getters.getCurrentRole);
const customRoleId = computed(() => store.getters.getCurrentCustomRoleId);
const customRoles = computed(() => store.getters['customRole/getCustomRoles'] || []);
const customRoleName = computed(() => {
  if (!customRoleId.value) return null;
  const role = customRoles.value.find(r => r.id === customRoleId.value);
  return role?.name || null;
});
const isSupervisor = computed(() => currentRole.value === 'agent' && customRoleName.value === 'Supervisor');
const isVendor = computed(() => currentRole.value === 'agent' && !isSupervisor.value);
const isAdmin = computed(() => currentRole.value === 'administrator');

// Form state
const contactSearch = ref('');
const contactId = ref(null);
const contactName = ref('');
const isNewContact = ref(false);
const inboxId = ref('');
const teamId = ref('');
const agentId = ref('');
const isCreating = ref(false);
const searchResults = ref([]);
const showResults = ref(false);
let searchTimer = null;

// Estado de conversa existente encontrada
const existingConversation = ref(null);
const existingAssignee = ref(null);

// Mensagem privada (vendedor → supervisor)
const privateMessage = ref('');
const selectedSupervisorId = ref(null);

// Data
const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);
const teams = computed(() => {
  if (isVendor.value) return store.getters['teams/getMyTeams'] || [];
  return store.getters['teams/getTeams'] || [];
});
const agents = computed(() => store.getters['agents/getAgents'] || []);

// Supervisores (para menção na mensagem privada)
const supervisors = computed(() => {
  const allAgents = store.getters['agents/getAgents'] || [];
  const roles = store.getters['customRole/getCustomRoles'] || [];
  const supervisorRole = roles.find(r => r.name === 'Supervisor');
  if (!supervisorRole) {
    // Fallback: mostrar admins
    return allAgents.filter(a => a.role === 'administrator');
  }
  return allAgents.filter(a =>
    a.custom_role_id === supervisorRole.id || a.role === 'administrator'
  );
});

onMounted(async () => {
  await Promise.all([
    store.dispatch('inboxes/get'),
    store.dispatch('teams/get'),
    store.dispatch('customRole/getCustomRole'),
    store.dispatch('agents/get'),
  ]);
});

const phoneRegex = /^[+]?[\d\s\-()]{8,15}$/;

// Busca via API direta (não afeta o store de contatos = não perde fotos na lista)
const onSearchInput = () => {
  if (searchTimer) clearTimeout(searchTimer);
  const query = contactSearch.value.trim();
  contactId.value = null;
  contactName.value = '';
  isNewContact.value = false;
  existingConversation.value = null;
  existingAssignee.value = null;
  privateMessage.value = '';
  selectedSupervisorId.value = null;

  if (query.length < 2) {
    showResults.value = false;
    return;
  }

  searchTimer = setTimeout(async () => {
    try {
      const { data } = await ContactAPI.search(query, 1, 'name', '');
      searchResults.value = (data.payload || []).slice(0, 10);
      showResults.value = true;
    } catch {
      searchResults.value = [];
      showResults.value = true;
    }
  }, 500);
};

// Ao selecionar um contato, verificar se tem conversa ativa
const selectContact = async (contact) => {
  contactId.value = contact.id;
  contactName.value = contact.name || contact.phone_number || '';
  contactSearch.value = contactName.value;
  isNewContact.value = false;
  showResults.value = false;
  existingConversation.value = null;
  existingAssignee.value = null;
  privateMessage.value = '';
  selectedSupervisorId.value = null;

  await checkExistingConversations(contact.id);
};

const createNewContact = () => {
  const phone = contactSearch.value.trim();
  contactId.value = null;
  contactName.value = phone;
  isNewContact.value = true;
  showResults.value = false;
  existingConversation.value = null;
  existingAssignee.value = null;
  privateMessage.value = '';
  selectedSupervisorId.value = null;
};

const isPhoneQuery = computed(() => phoneRegex.test(contactSearch.value.replace(/\s/g, '')));

// Esconder "Criar novo" se busca já encontrou contato com esse telefone
const phoneAlreadyExists = computed(() => {
  if (!isPhoneQuery.value) return false;
  const phone = contactSearch.value.replace(/\s/g, '');
  const normalized = phone.startsWith('+') ? phone : '+' + phone;
  return searchResults.value.some(c =>
    c.phone_number === phone || c.phone_number === normalized
  );
});

const canCreate = computed(() => {
  if (existingConversation.value) return false;
  return (contactId.value || isNewContact.value) && inboxId.value;
});

// Verificar conversas existentes de um contato
const checkExistingConversations = async (cid) => {
  try {
    await store.dispatch('contactConversations/get', cid);
    const convs = store.getters['contactConversations/getContactConversation'](cid) || [];
    const aberta = convs.find(c => c.status === 'open' || c.status === 'pending');
    if (aberta) {
      const assignee = aberta.meta?.assignee || null;

      // Vendedor + pendente (na fila sem ninguém): pegar pra si direto
      if (isVendor.value && aberta.status === 'pending' && !assignee) {
        await autoAssignToSelf(aberta);
        return;
      }

      existingConversation.value = aberta;
      existingAssignee.value = assignee;
    }
  } catch {
    // Se falhar, continua normalmente
  }
};

// Vendedor pega conversa pendente para si automaticamente
const autoAssignToSelf = async (conv) => {
  isCreating.value = true;
  try {
    await store.dispatch('toggleStatus', {
      conversationId: conv.id,
      status: 'open',
    });
    await store.dispatch('assignAgent', {
      conversationId: conv.id,
      agentId: currentUser.value.id,
    });
    if (teamId.value) {
      await store.dispatch('assignTeam', {
        conversationId: conv.id,
        teamId: parseInt(teamId.value),
      });
    }
    useAlert('Conversa atribuida a voce!');
    emit('close');
    router.push(`/app/accounts/${accountId.value}/conversations/${conv.id}`);
  } catch {
    useAlert('Erro ao atribuir conversa');
  } finally {
    isCreating.value = false;
  }
};

// Navegar para conversa existente
const goToExistingConversation = () => {
  if (!existingConversation.value) return;
  emit('close');
  router.push(`/app/accounts/${accountId.value}/conversations/${existingConversation.value.id}`);
};

// Transferir conversa existente (supervisor/admin)
const transferExistingConversation = async () => {
  if (!existingConversation.value) return;
  isCreating.value = true;
  try {
    const convId = existingConversation.value.id;
    if (existingConversation.value.status === 'pending') {
      await store.dispatch('toggleStatus', {
        conversationId: convId,
        status: 'open',
      });
    }
    const aid = agentId.value || currentUser.value.id;
    await store.dispatch('assignAgent', {
      conversationId: convId,
      agentId: parseInt(aid),
    });
    if (teamId.value) {
      await store.dispatch('assignTeam', {
        conversationId: convId,
        teamId: parseInt(teamId.value),
      });
    }
    useAlert('Conversa transferida com sucesso!');
    emit('close');
    router.push(`/app/accounts/${accountId.value}/conversations/${convId}`);
  } catch {
    useAlert('Erro ao transferir conversa');
  } finally {
    isCreating.value = false;
  }
};

// Vendedor: enviar mensagem privada mencionando supervisor na conversa existente
const sendPrivateMessageToSupervisor = async () => {
  if (!existingConversation.value || !privateMessage.value.trim() || !selectedSupervisorId.value) return;
  isCreating.value = true;
  try {
    const convId = existingConversation.value.id;
    const supervisor = supervisors.value.find(s => s.id === selectedSupervisorId.value);
    if (!supervisor) {
      useAlert('Supervisor nao encontrado');
      isCreating.value = false;
      return;
    }

    // Construir menção no formato do Chatwoot: (mention://user/ID/NOME)
    const mentionTag = `[@${supervisor.name}](mention://user/${supervisor.id}/${encodeURIComponent(supervisor.name)})`;
    const fullMessage = `${mentionTag} ${privateMessage.value.trim()}`;

    await MessageApi.create({
      conversationId: convId,
      message: fullMessage,
      private: true,
      echo_id: `ct-${Date.now()}`,
    });

    useAlert('Mensagem enviada ao supervisor!');
    emit('close');
  } catch {
    useAlert('Erro ao enviar mensagem');
  } finally {
    isCreating.value = false;
  }
};

const createConversation = async () => {
  if (!canCreate.value || isCreating.value) return;
  isCreating.value = true;

  try {
    let cid = contactId.value;

    // Criar contato novo ou buscar existente pelo telefone
    if (isNewContact.value) {
      let phone = contactName.value.replace(/\s/g, '');
      if (!phone.startsWith('+')) phone = '+' + phone;

      try {
        const res = await store.dispatch('contacts/create', {
          name: phone,
          phone_number: phone,
        });
        cid = res?.id || res?.payload?.contact?.id;
      } catch (err) {
        if (err instanceof DuplicateContactException) {
          // Telefone já existe — buscar via API direta
          const { data } = await ContactAPI.search(phone, 1, 'name', '');
          const contacts = data.payload || [];
          const existing = contacts.find(c => c.phone_number === phone);
          if (existing) {
            cid = existing.id;
            contactName.value = existing.name || phone;
            await checkExistingConversations(cid);
            if (existingConversation.value) {
              isCreating.value = false;
              return;
            }
          }
        }
        if (!cid) {
          useAlert('Erro ao criar contato: telefone pode ja estar em uso');
          isCreating.value = false;
          return;
        }
      }

      if (!cid) {
        useAlert('Erro ao criar contato');
        isCreating.value = false;
        return;
      }
    }

    // Verificar conversas existentes
    if (!existingConversation.value) {
      await checkExistingConversations(cid);
      if (existingConversation.value) {
        isCreating.value = false;
        return;
      }
    }

    // Buscar conversa fechada — reabrir e atribuir
    const allConvs = store.getters['contactConversations/getContactConversation'](cid) || [];
    const fechada = allConvs.find(c => c.status === 'resolved');
    if (fechada) {
      await store.dispatch('toggleStatus', {
        conversationId: fechada.id,
        status: 'open',
      });
      const aid = isVendor.value ? currentUser.value.id : (agentId.value || null);
      if (aid) {
        await store.dispatch('assignAgent', {
          conversationId: fechada.id,
          agentId: parseInt(aid),
        });
      }
      if (teamId.value) {
        await store.dispatch('assignTeam', {
          conversationId: fechada.id,
          teamId: parseInt(teamId.value),
        });
      }
      useAlert('Conversa reaberta com historico anterior!');
      emit('close');
      router.push(`/app/accounts/${accountId.value}/conversations/${fechada.id}`);
      return;
    }

    // Criar nova conversa
    const aid = isVendor.value ? currentUser.value.id : (agentId.value || null);
    const newConv = await store.dispatch('contactConversations/create', {
      params: {
        contactId: cid,
        inboxId: parseInt(inboxId.value),
        assigneeId: aid ? parseInt(aid) : undefined,
        message: { content: '' },
      },
    });

    const convId = newConv?.id;
    if (!convId) {
      useAlert('Erro: conversa criada mas sem ID');
      isCreating.value = false;
      return;
    }

    if (teamId.value) {
      await store.dispatch('assignTeam', {
        conversationId: convId,
        teamId: parseInt(teamId.value),
      });
    }

    useAlert('Conversa criada!');
    emit('close');
    router.push(`/app/accounts/${accountId.value}/conversations/${convId}`);
  } catch (err) {
    useAlert('Erro ao criar conversa');
  } finally {
    isCreating.value = false;
  }
};
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      @click.self="$emit('close')"
    >
      <div class="bg-n-background rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <!-- Header -->
        <div class="flex items-center justify-between px-5 py-4 border-b border-n-weak">
          <h3 class="text-base font-bold text-n-slate-12">Nova Conversa</h3>
          <button
            class="text-n-slate-8 hover:text-n-slate-12 transition-colors"
            @click="$emit('close')"
          >
            ✕
          </button>
        </div>

        <!-- Body -->
        <div class="px-5 py-4 space-y-4">
          <!-- Contato -->
          <div class="relative">
            <label class="text-xs font-semibold text-n-slate-11 mb-1 block">
              Contato (nome ou telefone)
            </label>
            <input
              v-model="contactSearch"
              type="text"
              placeholder="Buscar contato ou digitar telefone..."
              class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12 placeholder:text-n-slate-8 outline-none focus:border-n-brand"
              autocomplete="off"
              @input="onSearchInput"
              @focus="contactSearch.length >= 2 && (showResults = true)"
            />

            <!-- Resultados -->
            <div
              v-if="showResults"
              class="absolute left-0 right-0 top-full mt-1 bg-n-background border border-n-weak rounded-lg shadow-lg z-50 max-h-[200px] overflow-y-auto"
            >
              <button
                v-if="isPhoneQuery && !phoneAlreadyExists"
                class="flex items-center gap-2 w-full px-3 py-2 text-left text-sm text-emerald-600 font-semibold hover:bg-n-alpha-1 transition-colors"
                @click="createNewContact"
              >
                + Criar novo contato: <strong>{{ contactSearch }}</strong>
              </button>

              <button
                v-for="contact in searchResults"
                :key="contact.id"
                class="flex items-center w-full px-3 py-2 text-left text-sm hover:bg-n-alpha-1 transition-colors"
                @click="selectContact(contact)"
              >
                <span class="text-n-slate-12">{{ contact.name || 'Sem nome' }}</span>
                <span v-if="contact.phone_number" class="text-n-slate-8 ml-2">
                  {{ contact.phone_number }}
                </span>
              </button>

              <div
                v-if="searchResults.length === 0 && !isPhoneQuery"
                class="px-3 py-2 text-sm text-n-slate-8"
              >
                Nenhum contato encontrado
              </div>
            </div>

            <!-- Selecionado -->
            <div
              v-if="(contactId || isNewContact) && !existingConversation"
              class="mt-2 px-3 py-2 bg-blue-50 dark:bg-n-blue-2 rounded-lg text-sm text-blue-700"
            >
              {{ isNewContact ? 'Novo contato: ' : '' }}{{ contactName }}
            </div>

            <!-- CONVERSA EXISTENTE: Vendedor com outro vendedor -->
            <div
              v-if="existingConversation && isVendor && existingAssignee"
              class="mt-2 px-3 py-3 rounded-lg text-sm border bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200"
            >
              <div class="flex items-center gap-2 font-semibold mb-1">
                <div class="w-7 h-7 rounded-full bg-amber-200 dark:bg-amber-800 flex items-center justify-center text-xs font-bold text-amber-700 dark:text-amber-200 overflow-hidden flex-shrink-0">
                  <img
                    v-if="existingAssignee.avatar_url || existingAssignee.thumbnail"
                    :src="existingAssignee.avatar_url || existingAssignee.thumbnail"
                    class="w-full h-full object-cover"
                  />
                  <span v-else>{{ (existingAssignee.name || '?').charAt(0).toUpperCase() }}</span>
                </div>
                Contato em atendimento com {{ existingAssignee.name }}
              </div>
              <div class="text-xs mb-3 text-amber-600 dark:text-amber-400">
                Envie uma nota privada mencionando um supervisor para solicitar transferencia.
              </div>

              <!-- Select supervisor -->
              <select
                v-model="selectedSupervisorId"
                class="w-full px-3 py-1.5 text-sm border border-amber-300 dark:border-amber-700 rounded-lg bg-white dark:bg-n-solid-3 text-n-slate-12 outline-none mb-2"
              >
                <option :value="null" disabled>Marcar supervisor...</option>
                <option v-for="sup in supervisors" :key="sup.id" :value="sup.id">
                  {{ sup.name }}
                </option>
              </select>

              <!-- Mensagem privada -->
              <textarea
                v-model="privateMessage"
                rows="2"
                placeholder="Ex: Supervisor, pode transferir esse lead pra mim?"
                class="w-full px-3 py-2 text-sm border border-amber-300 dark:border-amber-700 rounded-lg bg-white dark:bg-n-solid-3 text-n-slate-12 placeholder:text-n-slate-8 outline-none resize-none mb-2"
              />

              <button
                :disabled="!privateMessage.trim() || !selectedSupervisorId || isCreating"
                class="w-full px-4 py-2 text-sm font-semibold rounded-lg transition-colors"
                :class="!privateMessage.trim() || !selectedSupervisorId
                  ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-amber-500 text-white hover:bg-amber-600'"
                @click="sendPrivateMessageToSupervisor"
              >
                {{ isCreating ? 'Enviando...' : 'Enviar nota ao supervisor' }}
              </button>
            </div>

            <!-- CONVERSA EXISTENTE: Vendedor + pendente COM assignee (raro) -->
            <div
              v-if="existingConversation && isVendor && !existingAssignee"
              class="mt-2 px-3 py-3 rounded-lg text-sm border bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200"
            >
              <div class="font-semibold mb-1">
                Este contato ja possui conversa ativa
              </div>
              <div>Na fila de pendentes (aguardando atendimento)</div>
            </div>

            <!-- CONVERSA EXISTENTE: Supervisor/Admin -->
            <div
              v-if="existingConversation && !isVendor"
              class="mt-2 px-3 py-3 rounded-lg text-sm border bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200"
            >
              <div class="font-semibold mb-1">
                Conversa ativa encontrada
              </div>
              <div v-if="existingAssignee" class="flex items-center gap-2 mb-2">
                <div class="w-6 h-6 rounded-full bg-blue-200 dark:bg-blue-800 flex items-center justify-center text-xs font-bold text-blue-700 dark:text-blue-200 overflow-hidden flex-shrink-0">
                  <img
                    v-if="existingAssignee.avatar_url || existingAssignee.thumbnail"
                    :src="existingAssignee.avatar_url || existingAssignee.thumbnail"
                    class="w-full h-full object-cover"
                  />
                  <span v-else>{{ (existingAssignee.name || '?').charAt(0).toUpperCase() }}</span>
                </div>
                Atribuida a: <strong>{{ existingAssignee.name }}</strong>
              </div>
              <div v-else class="mb-2">
                Na fila (sem atendente)
              </div>
              <div class="flex gap-2 mt-2">
                <button
                  class="px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  @click="goToExistingConversation"
                >
                  Abrir conversa
                </button>
                <button
                  class="px-3 py-1.5 text-xs font-semibold bg-amber-500 text-white rounded-md hover:bg-amber-600 transition-colors"
                  :disabled="isCreating"
                  @click="transferExistingConversation"
                >
                  {{ isCreating ? 'Transferindo...' : 'Transferir pra mim' }}
                </button>
              </div>
            </div>
          </div>

          <!-- Inbox -->
          <div v-if="!existingConversation">
            <label class="text-xs font-semibold text-n-slate-11 mb-1 block">
              Conexao (Inbox)
            </label>
            <select
              v-model="inboxId"
              class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12 outline-none focus:border-n-brand"
            >
              <option value="">Selecione...</option>
              <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
                {{ inbox.name }}
              </option>
            </select>
          </div>

          <!-- Time -->
          <div v-if="!existingConversation || (!isVendor && existingConversation)">
            <label class="text-xs font-semibold text-n-slate-11 mb-1 block">
              Time (opcional)
            </label>
            <select
              v-model="teamId"
              class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12 outline-none focus:border-n-brand"
            >
              <option value="">Nenhum</option>
              <option v-for="team in teams" :key="team.id" :value="team.id">
                {{ team.name }}
              </option>
            </select>
          </div>

          <!-- Agente (supervisor/admin) -->
          <div v-if="!isVendor">
            <label class="text-xs font-semibold text-n-slate-11 mb-1 block">
              Agente (opcional)
            </label>
            <select
              v-model="agentId"
              class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12 outline-none focus:border-n-brand"
            >
              <option value="">Nenhum (nao atribuir)</option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex justify-end gap-2 px-5 py-4 border-t border-n-weak">
          <button
            class="px-4 py-2 text-sm font-medium text-n-slate-11 hover:text-n-slate-12 rounded-lg hover:bg-n-alpha-2 transition-colors"
            @click="$emit('close')"
          >
            Cancelar
          </button>
          <button
            v-if="!existingConversation"
            :disabled="!canCreate || isCreating"
            class="px-4 py-2 text-sm font-semibold rounded-lg transition-all"
            :class="!canCreate || isCreating
              ? 'bg-n-slate-3 text-n-slate-8 cursor-not-allowed'
              : 'bg-woot-500 text-white hover:bg-woot-600'"
            @click="createConversation"
          >
            {{ isCreating ? 'Criando...' : 'Criar' }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
